import { useState } from "react";
import { useAuth } from "@/context/AuthContext";

function getHashError(): string | null {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  const code = params.get("error_code");
  if (code === "otp_expired") {
    return "El enlace de recuperación expiró o ya fue usado. Solicita uno nuevo.";
  }
  if (params.get("error")) {
    return "El enlace de recuperación no es válido. Solicita uno nuevo.";
  }
  return null;
}

function MailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="1.7" />
      <path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
      {!open && <path d="m4 4 16 16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />}
    </svg>
  );
}

export default function LoginPage() {
  const { signIn, resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Password recovery is shown inside the card instead of an overlay:
  // Excel's task pane webview can paint blurred overlays as a black screen.
  const [view, setView] = useState<"login" | "reset">("login");
  const [resetEmail, setResetEmail] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [resetNotFound, setResetNotFound] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const hashError = getHashError();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) setError(error);
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetMsg("");
    setResetNotFound(false);
    setResetLoading(true);
    const { error, notFound } = await resetPassword(resetEmail);
    setResetLoading(false);
    if (error) {
      setResetMsg(error);
      setResetNotFound(!!notFound);
    } else {
      setResetMsg("Te enviamos un enlace de recuperación. Revisa tu correo.");
    }
  };

  const openReset = () => {
    setResetEmail(email);
    setResetMsg("");
    setResetNotFound(false);
    setView("reset");
  };

  return (
    <div className="auth-page">
      <div className="auth-card" key={view}>
        <div className="auth-logo">
          <img src="/images/Excel-Logo.png" alt="Hazlo Con Excel" />
        </div>

        {view === "login" ? (
          <>
            <h1 className="auth-title">Modo Imparable</h1>
            <p className="auth-subtitle">
              Aprende Excel resolviendo proyectos reales directamente en tu hoja de cálculo.
            </p>

            <ul className="auth-benefits">
              <li>Proyectos guiados paso a paso con instrucciones claras.</li>
              <li>Calificación automática: revisamos tu libro y te decimos qué corregir.</li>
              <li>Tu cuenta te da acceso a todas las prácticas, con modo práctica y modo examen.</li>
            </ul>

            {hashError && <div className="error-msg">{hashError}</div>}
            {error && <div className="error-msg">{error}</div>}

            <form className="auth-form" onSubmit={handleLogin}>
              <label className="auth-label" htmlFor="login-email">
                Correo electrónico
              </label>
              <div className="auth-field">
                <span className="auth-field-icon"><MailIcon /></span>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  placeholder="tucorreo@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="auth-label-row">
                <label className="auth-label" htmlFor="login-password">
                  Contraseña
                </label>
                <button type="button" className="auth-text-btn" onClick={openReset}>
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="auth-field">
                <span className="auth-field-icon"><LockIcon /></span>
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading && <span className="btn-spinner" />}
                {loading ? "Ingresando..." : "Iniciar sesión"}
              </button>
            </form>

            <div className="auth-divider"><span>¿Aún no tienes cuenta?</span></div>

            <a
              className="auth-signup-btn"
              href="mailto:soporte@hazloconexcel.com?subject=Quiero%20mi%20cuenta%20de%20Modo%20Imparable"
            >
              Solicítala a soporte@hazloconexcel.com
            </a>
          </>
        ) : (
          <>
            <h1 className="auth-title">Recuperar contraseña</h1>
            <p className="auth-subtitle">
              Escribe tu correo y te enviaremos un enlace para crear una nueva.
            </p>

            <form className="auth-form" onSubmit={handleResetPassword}>
              <label className="auth-label" htmlFor="reset-email">
                Correo electrónico
              </label>
              <div className="auth-field">
                <span className="auth-field-icon"><MailIcon /></span>
                <input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  placeholder="tucorreo@ejemplo.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              {resetMsg && !resetNotFound && (
                <p className={resetMsg.startsWith("Te enviamos") ? "reset-success-msg" : "error-msg"}>
                  {resetMsg}
                </p>
              )}
              {resetNotFound && (
                <p className="error-msg">
                  No encontramos una cuenta con ese correo. Escríbenos a{" "}
                  <a href="mailto:soporte@hazloconexcel.com">soporte@hazloconexcel.com</a>{" "}
                  para ayudarte.
                </p>
              )}

              <button type="submit" className="auth-submit" disabled={resetLoading}>
                {resetLoading && <span className="btn-spinner" />}
                {resetLoading ? "Enviando..." : "Enviar enlace"}
              </button>
            </form>

            <button type="button" className="auth-back" onClick={() => setView("login")}>
              ← Volver a iniciar sesión
            </button>
          </>
        )}
      </div>

      <footer className="auth-footer">
        <a href="/soporte" target="_blank" rel="noopener noreferrer">Soporte</a>
        <span aria-hidden="true">·</span>
        <a href="/privacidad" target="_blank" rel="noopener noreferrer">Privacidad</a>
      </footer>
    </div>
  );
}
